import React, { FC, useEffect, useRef, useState } from "react";
import * as d3 from "d3";

import { Theme, useTheme } from "@mui/material/styles";
import { Box } from "@mui/material";

import { Category, DataPoint } from "fileParsing/types";
import { SimulationLinkDatum, SimulationNodeDatum } from "d3";
import useLanguageHook from "containers/hooks/useLanguageHook";
import { ResizeComponent, SizeProps } from "../hooks/ResizeComponent";
import { useDatapointsFromExportId } from "../hooks/useData";
import { useAppDispatch, useAppSelector } from "../../index";
import { updateContacts } from "@store/filters/actions";
import Tooltip from "../Components/Tooltip";

/* ****************************************************************** */
// interfaces for some specific obejects
/* ****************************************************************** */
/**
 * @type Contact
 * One Contact per Datapoint of the message category
 * it is included in a Link
 */
type Contact = {
  date: Date;
  category: Category; // now it is used just as messages but extensible to other category later
  provider: string;
};
/**
 * @type Link
 * A link contains all contacts between sender(source) and receiver(target)
 * With the given source and target, there is only 1 link
 * But there can be 2 link between two people, because this link is directed.
 */
type Link = {
  source: Node;
  target: Node;
  contacts: Contact[];
} & SimulationLinkDatum<Node>;
/**
 * @type Node
 * Node will be drawn as circle {x, y, radius, color}
 * and it has all links relative with itself (as sender and also receiver)
 * contacts : the number of all contacts
 * selected : whether it is selected in the drawn graph
 */
type Node = {
  // properties for drawing
  id: string;
  x: number;
  y: number;
  radius: number;
  color: string;
  selected: boolean;

  // properties for information
  links: Link[];
  contacts: number;
  numOfFriends?: number;
  category: Category;
  countCategories: Map<Category, number>;
  countProvider: Map<string, number>;
} & SimulationNodeDatum;

/**
 * @type NetworkFilter
 * from, to : time interval
 * min, max : frequency interval
 */
type NetworkFilter = {
  min: number | undefined;
  max: number | undefined;
};
/* ****************************************************************** */
// funtctions related with Graph
/* ****************************************************************** */
function makeContact(date: Date, category: Category, provider: string) {
  return {
    date,
    category,
    provider,
  };
}
function makeNode(id: string): Node {
  const counterCat = new Map<Category, number>();
  const counterProv = new Map<string, number>();
  for (const cat in Category) counterCat.set(cat as Category, 0);
  for (const prov in [
    "discord",
    "facebook",
    "google",
    "intagram",
    "instagram_old",
    "linkedin",
    "netflix",
    "reddit",
    "tiktok",
    "twitter",
  ])
    counterProv.set(prov, 0);

  return {
    id,
    x: 0,
    y: 0,
    radius: 1,
    links: [],
    contacts: 0,
    category: Category.Other,
    countCategories: counterCat,
    countProvider: counterProv,
    color: "black",
    selected: false,
  };
}
function makeLink(source: Node, target: Node): Link {
  return {
    source,
    target,
    contacts: [],
  };
}

/* ****************************************************************** */
// Data preprocessing
/* ****************************************************************** */
/**
 * generate graph data from data point
 * @param data  the loaded array of data points
 */
const generateGraphData = (data: DataPoint[]) => {
  const nodes: Node[] = [],
    links: Link[] = [];

  // now just for Message Category
  data
    .filter((d) => d.category === Category.Messages)
    .forEach((d: DataPoint) => {
      let sender: Node;
      let receiver: Node;
      let contact: Contact;
      let link: Link | undefined;

      // generate nodes
      // find the nodes with given name
      const friends: Node[] = (d.contacts || []).map((name) => {
        let node = nodes.find((n) => n.id === name);
        // add new node if it doesn't exist
        if (node === undefined) {
          node = makeNode(name);
          nodes.push(node);
        }
        return node;
      });

      // generate links
      // one-to-one message
      if (friends.length === 2) {
        sender = friends[0];
        receiver = friends[1];
        contact = makeContact(d.date, d.category, "Facebook");
        // find a link between sender and receiver if it already exists
        link = links.find((l) => l.source.id === sender.id && l.target.id === receiver.id);
        // otherwise, make and add new link to links and each node
        if (!link) {
          link = makeLink(sender, receiver);
          links.push(link);
          // connected each other
          sender.links.push(link);
          receiver.links.push(link);
        }
        // new contact is added to this link
        link.contacts.push(contact);
      }
    });
  // initialize all nodes
  nodes.forEach((n) => {
    setNodeContactInfo(n);
    setNodeColor(n);
  });
  // initialize root node

  return nodes;
};

/**
 * set countCategories, countProvider and contacts of node
 * @param node
 */
function setNodeContactInfo(node: Node) {
  node.links.forEach((l) =>
    l.contacts.forEach((c) => {
      node.countCategories.set(c.category, (node.countCategories?.get(c.category) || 0) + 1); // categories
      node.countProvider.set(c.provider, (node.countProvider.get(c.provider) || 0) + 1); // provides
    }),
  );
  // contacts : the number of contacts related with the node
  node.contacts = node.links.reduce((sum, l) => sum + l.contacts.length, 0);
}

/**
 * the color of node is determinde by the category with the most contacts
 * @param node
 */
function setNodeColor(node: Node) {
  let maxKey = Category.Other;
  let maxValue = 0;
  node.countCategories.forEach((value, key) => {
    if (maxValue < value) {
      maxValue = value;
      maxKey = key;
    }
  });

  node.category = maxKey;
}

/**
 * get domain [min, max] of contacts that each link has
 * @param links
 * @returns [min, max]
 */
function getContactsDomain(links: Link[]) {
  return d3.extent(links.map((l) => l.contacts.length));
}

/**
 * Get a range data of the link's length
 * step : (minimum length of the given width and height) / 10
 * start length : 2* step + 10, near by root node
 * end length : minLen - step*3, near by canvas's border

 * @param width      the width of canvas on where network graph is drawn
 * @param height     the height of it
 * @returns range data
 */
function getLinkLengthRange(width: number, height: number) {
  const range = [];
  const minLen = (width < height ? width : height) / 2;
  const step = minLen / 10; // The step is set as minimum length / 10
  const availableLen = minLen - step * 3; // the limit of the link's length

  // until the limit, generate new range's value by step
  // it'll make the layers for positions of nodes.
  for (let i = step * 2 + 10; i < availableLen; i += step) range.push(i);
  return range;
}

/**
 * As domain interval is divided by range size, each separate interval can be obtain (I call it layer)
 * Each link is only on one specific layer
 * After the layer for the given link is found, return the range's value of the layer.
 * It is the length of the given link.
 * @param link
 * @param domain Contacts domain
 * @param range  link length range
 * @returns the determined length of link
 */
function setLinkLength(link: Link, domain: [undefined, undefined] | [number, number], range: number[]) {
  // domain : [min, max] of the number of contacts
  if (domain[0] && domain[1]) {
    const contacts = link.contacts.length;
    // start from the end of range, because the length of links get shorter as contacts increase
    let ridx = range.length - 1;
    // step is set as below
    // it means, the length of links belongs to one of the layers as many as step.
    const step = (domain[1] - domain[0]) / range.length;
    // it is the limit of each layer
    let boundary = domain[0];
    for (ridx; ridx > 0; ridx--) {
      boundary += step; // move to next layer
      if (contacts < boundary) break; // if the link belongs to this layer, return the length of this layer
    }
    return range[ridx];
  } else return range[range.length - 1];
}
/**
 * As domain interval is divided by range size, each separate interval can be obtain (I call it layer)
 * Each node is only on one specific layer
 * After the layer for the given node is found, set the node size with the range's value of the layer.
 * Iterate all nodes.
 * @param nodes
 * @param domain Contacts domain
 * @param range of the node sizes [min, max]
 */
function setAllNodeSize(nodes: Node[], domain: [undefined, undefined] | [number, number], range: number[]) {
  if (domain[0] && domain[1]) {
    nodes.forEach((n) => {
      // start from the first element of range, because the node size get bigger as contacts increase
      let idx = 0;
      // step is set as below
      // it means, the node size belongs to one of the layers as many as step.
      const step = (domain[1] - domain[0]) / range.length;
      // it is the limit of each layer
      let boundary = domain[0];
      for (idx; idx < range.length; idx++) {
        boundary += step; // move to next layer
        if (n.contacts < boundary) break; // if the link belongs to this layer, stop
      }
      n.radius = idx * 2; // set the node size with the range value of it's layer.
    });
  }
}

/* ****************************************************************** */
// Filter Functions
/* ****************************************************************** */
const filterContacts = (nodes: Node[], f: NetworkFilter) => {
  nodes.forEach((node) => {
    node.links = node.links.filter(
      (link) =>
        f.min !== undefined && f.max !== undefined && link.contacts.length >= f.min && link.contacts.length <= f.max,
    );
  });

  return nodes.filter((node) => node.links.length > 0);
};

/* ****************************************************************** */
// Draw Functions
/* ****************************************************************** */
/**
 * draw a link on cavnas
 * if the link is connected to selected nodes, draw it bigger with the different color
 * @param context cavnas where a link is drawn on
 * @param link link to be drawn
 */
function drawLink(context: CanvasRenderingContext2D, link: Link) {
  context.save();
  context.beginPath();
  // if a link is connected to selected nodes
  if (link.source.selected && link.target.selected) {
    // draw it bigger with the different color
    context.lineWidth = 3;
    context.strokeStyle = "gold";
  }
  context.moveTo(link.source.x, link.source.y);
  context.lineTo(link.target.x, link.target.y);
  context.stroke();
  context.restore();
}
/**
 *  * draw a node on cavnas
 * if the node is selected, draw it bigger with the different color
 * @param context cavnas where a link is drawn on
 * @param node node to be drawn
 * @param theme
 */
function drawNode(context: CanvasRenderingContext2D, node: Node, theme: Theme) {
  let radius = node.radius;
  context.save();
  context.fillStyle = theme.palette.categories[node.category].main;

  context.strokeStyle = "black"; // default stroke color
  // if the node is selected
  if (node.selected) {
    // draw it bigger with the different color
    context.strokeStyle = "gold";
    radius *= 1.2;
  }
  context.beginPath();
  context.moveTo(node.x + node.radius, node.y);
  context.arc(node.x, node.y, radius, 0, 2 * Math.PI);
  context.fill();
  context.stroke();

  // draw the name of friend
  context.fillStyle = theme.palette.text.primary;
  context.font = `${node.radius / 2}px`;
  context.fillText(node.id.split(" ")[0], node.x, node.y + node.radius + 10); // node id is written under the node
  context.restore();
}

/* ****************************************************************** */
// Settings
/* ****************************************************************** */
export type NetworkSettings = {
  width: number;
  height: number;
  filterWidth: number;
  filterHeight: number;
  infoHeight: number;

  margin: number;
  padding: number;

  linkStroke: string;
  linkStrokeWidth: number;
};
const defaultSettings: NetworkSettings = {
  width: 1300,
  height: 800,
  filterWidth: 370,
  filterHeight: 250,
  infoHeight: 200,

  margin: 10,
  padding: 10,

  linkStroke: "gray",
  linkStrokeWidth: 1,
};

// Component-Specific props.
type ComponentProps = {
  exportIds?: number[];
  settings?: Partial<NetworkSettings>;
};

/**
 * @param props see NetworkSettings
 * @returns React.FC rendering
 */
const Network: FC<ComponentProps & SizeProps> = (props: ComponentProps & SizeProps) => {
  // canvas
  const colorCanvasRef = useRef<HTMLCanvasElement>(null);
  const interactCanvasRef = useRef<HTMLCanvasElement>(null);
  const zoomRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useLanguageHook();

  const contactRangeFilter = useAppSelector((state) => state.filters.contactFilterRange);

  // settings
  const settings = {
    ...defaultSettings,
    ...props.settings,
    height: props.size.height,
    width: props.size.width,
  };
  const canvasWidth = settings.width - 2 * settings.margin;
  const canvasHeight = settings.height;

  // Data;
  const fullData = useDatapointsFromExportId(props.exportIds).filter(({ contacts }) => (contacts?.length ?? 0) > 0);

  // -----------------------------------------------------------------------------------------------------
  // States
  // -----------------------------------------------------------------------------------------------------
  const [hoveredNode, setHoveredNode] = useState<{ node: Node; pageCoord: { x: number; y: number } } | undefined>(
    undefined,
  );

  const [graph, setGraph] = useState<{
    nodes: Node[];
    links: Link[];
  }>({
    nodes: [],
    links: [],
  });

  const simulation = React.useRef<d3.Simulation<Node, Link | undefined>>(d3.forceSimulation());

  // -----------------------------------------------------------------------------------------------------
  // Effects
  // -----------------------------------------------------------------------------------------------------
  // initial setup : filter, tooltip
  useEffect(() => {
    // Update Graph
    const nodes = generateGraphData(fullData);

    const filteredNodes = filterContacts(nodes, contactRangeFilter); // apply filter to graph
    const filteredLinks = filteredNodes.map((fnode) => fnode.links).flat();

    // get domain and range
    const contactsDomain = getContactsDomain(filteredLinks);
    const linkLenRange = getLinkLengthRange(canvasWidth, canvasHeight / 2);

    // set all node size based on domain and range
    setAllNodeSize(filteredNodes, contactsDomain, linkLenRange);

    setGraph({
      nodes: filteredNodes,
      links: filteredLinks,
    });

    // Update #-Messages-Filter
    const maxContacts = Math.max(
      ...generateGraphData(fullData)
        .map((n) => n.links.map((l) => l.contacts.length))
        .flat(),
    );

    if (maxContacts === -Infinity) return;

    let newMin = contactRangeFilter.min;
    let newMax = contactRangeFilter.max;

    if (contactRangeFilter.min < 1 || contactRangeFilter.min > maxContacts) newMin = 1;
    if (contactRangeFilter.max < newMin || contactRangeFilter.max > maxContacts) newMax = maxContacts;

    dispatch(updateContacts({ min: newMin, max: newMax }));
  }, [fullData.length, contactRangeFilter.min, contactRangeFilter.max]);

  useEffect(
    () => renderNetworkChart(),
    [graph.nodes.length, graph.links.length, settings.width, settings.height, theme],
  );

  // -----------------------------------------------------------------------------------------------------
  // functions : draw Chart
  // -----------------------------------------------------------------------------------------------------
  const renderNetworkChart = () => {
    if (colorCanvasRef.current) {
      const ctx = colorCanvasRef.current.getContext("2d");
      if (ctx) drawContactNetwork(ctx);
    }
  };
  /**
   * (1) define forces and the link's length
   * (2) define simulation with them
   * (3)
   * @param ctx canvas on that graph is drawn
   */
  const drawContactNetwork = (ctx: CanvasRenderingContext2D) => {
    let transform: d3.ZoomTransform = d3.zoomIdentity;
    // the link's length is determined by setLinkLength function with domain, range
    const forceLink = d3
      .forceLink(graph.links)
      .id(({ index: i }) => (i !== undefined && graph.nodes[i] ? graph.nodes[i].id : ""))
      .distance((l) =>
        setLinkLength(l, getContactsDomain(graph.links), getLinkLengthRange(canvasWidth, canvasHeight / 2)),
      );

    // force
    simulation.current
      .nodes(graph.nodes)
      .force("link", forceLink)
      .force("charge", d3.forceManyBody().strength(-50))
      .force("center", d3.forceCenter(canvasWidth / 2, canvasHeight / 2))
      .force("x", d3.forceX(canvasWidth / 2).strength(0.05))
      .force("y", d3.forceY(canvasHeight / 2).strength(0.05));
    simulation.current.on("tick", ticked);

    // attach zoom & drag
    if (interactCanvasRef.current) {
      const zoom = d3
        .zoom<HTMLCanvasElement, unknown>()
        .scaleExtent([0.8, 3])
        .extent([
          [0, 0],
          [canvasWidth, canvasHeight],
        ])
        .on("zoom", canvasZoomed);

      const drag = d3
        .drag<HTMLCanvasElement, unknown, unknown>()
        .container(interactCanvasRef.current)
        .subject(dragsubject)
        .on("start", dragstarted)
        .on("drag", dragged)
        .on("end", dragended);

      d3.select(interactCanvasRef.current).call(drag).call(zoom);
      simulation.current.alphaTarget(0.05).restart();
    }

    // add mouse-event-handlers on interactive canvas
    d3.select(interactCanvasRef.current).on("mousemove", (e) => {
      // when mouse is on a node, the node become activeNode to get the reference to it.
      const d = simulation.current.find(transform.invertX(e.offsetX), transform.invertY(e.offsetY), 10);

      if (d) {
        if (hoveredNode?.node?.id !== d.id)
          setHoveredNode({
            node: d,
            pageCoord: { x: e.pageX ?? 0, y: e.pageY ?? 0 },
          });
      } else {
        setHoveredNode(undefined);
      }
    });

    // it draws the graph by simulation
    function ticked() {
      if (ctx) {
        ctx.save();
        ctx.clearRect(0, 0, settings.width, canvasHeight); // clear previous drawing
        // apply zoom to canvas
        ctx.translate(transform.x, transform.y);
        ctx.scale(transform.k, transform.k);
        // draw Link
        ctx.strokeStyle = settings.linkStroke;
        ctx.lineWidth = settings.linkStrokeWidth;
        graph.links.forEach((l) => drawLink(ctx, l));
        ctx.restore();
        // draw Node
        ctx.save();
        ctx.translate(transform.x, transform.y);
        ctx.scale(transform.k, transform.k);
        ctx.textAlign = "center";
        graph.nodes.forEach((n) => drawNode(ctx, n, theme));

        ctx.restore();
      }
    }
    function canvasZoomed(e: d3.D3ZoomEvent<HTMLCanvasElement, unknown>) {
      transform = e.transform;
      ticked(); // redraw
    }
    function dragsubject(e: d3.D3DragEvent<HTMLCanvasElement, unknown, unknown>) {
      const node = simulation.current.find(transform.invertX(e.x), transform.invertY(e.y), 50);
      if (node) {
        node.x = transform.applyX(node.x);
        node.y = transform.applyY(node.y);
      }
      return node;
    }
    function dragstarted(e: d3.D3DragEvent<HTMLCanvasElement, unknown, Node | undefined>) {
      if (!e.active) simulation.current.alphaTarget(0.3).restart();
      if (e.subject) {
        e.subject.fx = transform.invertX(e.subject.x);
        e.subject.fy = transform.invertY(e.subject.y);
      }
    }
    function dragged(e: d3.D3DragEvent<HTMLCanvasElement, unknown, Node | undefined>) {
      if (e.subject) {
        e.subject.fx = transform.invertX(e.x);
        e.subject.fy = transform.invertY(e.y);
      }
    }
    function dragended(e: d3.D3DragEvent<HTMLCanvasElement, unknown, Node | undefined>) {
      if (!e.active) simulation.current.alphaTarget(0);
      if (e.subject) {
        e.subject.fx = null;
        e.subject.fy = null;
      }
    }
  };
  // Output
  return (
    <Box
      ref={zoomRef}
      sx={{
        width: canvasWidth,
        height: canvasHeight,
        position: "relative",
        marginX: `${settings.margin}px`,
      }}
    >
      <canvas
        ref={colorCanvasRef}
        height={canvasHeight}
        width={canvasWidth}
        style={{
          pointerEvents: "none",
          position: "absolute",
          border: "1px solid black",
        }}
      />
      <canvas
        ref={interactCanvasRef}
        height={canvasHeight}
        width={canvasWidth}
        style={{
          position: "absolute",
        }}
      />
      <Tooltip
        visible={hoveredNode !== undefined && hoveredNode?.pageCoord?.x !== 0 && hoveredNode?.pageCoord?.y !== 0}
        isFixed={false}
        position={{
          x: hoveredNode?.pageCoord?.x ?? 0,
          y: hoveredNode?.pageCoord?.y ?? 0,
        }}
        settings={{ tooltipWidth: 250, margin: 0 }}
        texts={{
          title: hoveredNode?.node.category ?? Category.Other,
          subtitle: "",
          body: `${t.Vis.NumberExchangedMessagesWith} ${hoveredNode?.node.id}: ${
            hoveredNode?.node.links.reduce((counter, l) => counter + l.contacts.length, 0) ?? 0
          }`,
        }}
        category={hoveredNode?.node.category ?? Category.Other}
      />
    </Box>
  );
};

export default ResizeComponent(Network);
